import React, { useCallback, useEffect, useState, useContext, useRef } from 'react';
import { ProviderConsumer as FluentUIThemeConsumer } from '@fluentui/react-northstar';
import { TicketToolbar } from './toolbar/TicketToolbar';
import { TicketBoardView } from './TicketBoardView/TicketBoardView';
import { TicketBoardMobileView } from './TicketBoardView/TicketBoardMobileView';
import {
  filterOptions,
  ListInputItemsEnum,
  loadFilters,
  toggleButtonMapper,
  ViewType
} from './ticketHelper';
import { AccessDenied } from '../shared/components/AccessDenied';
import _ from 'lodash';
import { appState, useSetState } from '../AppState';
import { platformService } from '../shared/services/platform.service';
import { ConsentState, SignedInConsentState } from '../shared/services/signInState.service';
import { Helmet } from 'react-helmet';
import { checkInTeams } from '../App';
import { BulkEditTicketDialog } from '../shared/components/BulkEditTicketDialog/BulkEditTicketDialog';
import { BulkDeleteConfirmDialog } from '../shared/components/BulkEditTicketDialog/BulkDeleteConfirmDialog';
import { getCachedFeature } from '../shared/cache/FeatureCache';
import { CONTACT_ADMIN, LICENSE_FEATURE, PRODUCT_LICENSE, SOMETHING_WENT_WRONG, UNAUTHORIZED_ACCESS, customClassNames, ConfigurationName, ADMINISTRATORS, CustomViewType, ANALYSTS, DefaultViewGuid } from '../shared/utils/constants';
import { Ticket } from '../shared/interfaces/ticket.interface';
import ListContextProvider, { ListContextType } from './List/ListContextProvider';
import ListFunctions from './List/ListFunctions';
import TicketToolbarSecondary from './toolbar/TicketToolbarSecondary';
import { CustomViewContext, DEFAULT_SORT_ORDER } from './toolbar/CustomViewContextProvider';
import CustomViewPopup from './toolbar/CustomViewPopup';
import useWindowWidth from './useWindowWidth';
import { graphService } from '../shared/services/graph.service';
import { addUrlSearchParams, getChildFiltersCountFromStorage, getChildFiltersInputValueFromStorage, getColumnsSelectedValues, getCustomViewDropdownFilters, getViewIdFromStorage } from './toolbar/CustomViewPopup/heplers/customPopupHelper';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { prepareFiltersData } from './List/TicketQueryHelper';
import { ScopeErrorMessage } from '../Rbac/ScopePermissionNoAccess';
import { setSideViewFilterIndex } from './EditTicket/storageKeys';
import eventBus from '../shared/services/eventBus.service';




interface Props {
  page: string | undefined;
}

export const Tickets = ({ page }: Props) => {
  const {t} = useTranslation();
  const api = new platformService();
  const graphAPI = new graphService();
  const [isSignedIn] = SignedInConsentState.useIsSignedIn();
  const setState = useSetState();
  const history = useHistory();
  const location = useLocation();
  const currentstate = appState();
  const { customViewStates, setCustomViewStates } = useContext(CustomViewContext);
  const [filters, setFilters] = useState<FilterPropsItems[]>(filterOptions);
  const [defaultFilters, setDefaultFilters] = useState<FilterPropsItems[]>(filterOptions);
  const [hasAccess, setAccess] = useState(true);
  const [isCustomViewEnabled, setIsCustomViewEnabled] = useState(false);
  const [isScopingEnabled, setIsScopingEnabled] = useState(false);
  const [isTicketsFetching, setIsTicketsFetching] = useState(true);
  const [isFiltersFetching, setIsFiltersFetching] = useState(true);
  const [bulkEditDialog, setBulkEditDialog] = useState(false);
  const [bulkDeleteDialog, setBulkDeleteDialog] = useState(false);
  const [selectedTickets, setSelectedTickets] = useState<Ticket[]>([]);
  const [isUserLicensed, setIsUserLicensed] = useState<boolean>(false);
  const [canDeleteTickets, setCanDeleteTickets] = useState(false);
  const [listFeatureStates, setListFeatureStates] = useState<ListFeatureStates>();
  const abortControllerRef = useRef<AbortController | null>(null);

  const isReloadClickedRef = useRef(false);
  const prevPageRef = useRef(page);
  const windowWidth = useWindowWidth()

  const [showBoard, setShowBoard] = useState<boolean>(true);

  const { getpaginatedTicketsOnEdit } = ListFunctions();
  let lastSelectedViewId = getViewIdFromStorage('custom');

  const setError = (e: any) => {
    setAccess(e.response && e.response.status === 403);
    const errCode = e.response ? e.response.status : 500;
    let errorstate = {
      ...currentstate,
      isLoading: false,
      isError: true,
      errorConfig: {
        fields: {
          title:
            errCode == 401 || errCode == 403
              ? UNAUTHORIZED_ACCESS
              : SOMETHING_WENT_WRONG,
          desc: CONTACT_ADMIN
        }
      }
    };
    if (!_.isEqual(currentstate, errorstate)) {
      setState(errorstate);
    }
  };


  const fetchUserData = useCallback(async () => {
    try {
      const result = await api.meAuthenticate();

      let userIdClaim = result.data.Claims.find((claim: any) => claim.startsWith('preferred_username:'));
      let userEmail = userIdClaim?.split('preferred_username: ')[1] ?? '';
    
      const newState = { userImages: {}, currentUserEmail: userEmail };
      setState(prevState => ({ ...prevState, ...newState }));
    } catch (err) {
      console.log(`Error occurred when loading current user:`, err);
      setError(err);
    }
  }, []);

  
  const fetchCustomViews = async () => {
    try {

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      const isEnabled = await getCachedFeature(LICENSE_FEATURE.CustomViews);
      const isScopingEnabled = await getCachedFeature(LICENSE_FEATURE.PermissionScoping);
      setIsCustomViewEnabled(isEnabled);
      setIsScopingEnabled(isScopingEnabled);
      if (isEnabled) {
        const customViewsList = await api.getCustomViewsSafe();
        
        //api.getCustomViewsSafe() takes long time when there are many custom views, 
        // so we check if the request is aborted before proceeding to set states and url params. 
        // This will avoid unnecessary state updates and url changes when user quickly toggles between pages or clicks reload multiple times.
        if (abortController.signal.aborted) return; //this 

        let selectedView = customViewsList.find(x=> lastSelectedViewId ? x.Id==lastSelectedViewId : x.Type === CustomViewType.DefaultView);
        if (selectedView==undefined) {
          selectedView = customViewsList.find(x=> x.Type === CustomViewType.DefaultView);
          if (selectedView==undefined) {
            selectedView = customViewsList.find(x=> x.Guid === DefaultViewGuid); //Default view guid
          }
        }

        const { Id, Name, ViewFilters, ViewColumns, OrderBy, OrderType, Type } = selectedView;
        const FiltersDropdownValues = getCustomViewDropdownFilters(JSON.parse(JSON.stringify(defaultFilters)), ViewFilters);
        const ColumnsSelectedValues = getColumnsSelectedValues(selectedView.ViewColumns);
        setCustomViewStates((prev): CustomViewStatesType => 
        ({ 
          ...prev, 
          customViews: customViewsList,
          selectedCustomView: {
            Id,
            Name,
            ViewFilters,
            ViewColumns,
            FiltersDropdownValues,
            ColumnsSelectedValues,
            Type,
            OrderBy: OrderBy || DEFAULT_SORT_ORDER?.orderBy,
            OrderType: OrderType || DEFAULT_SORT_ORDER?.orderType,
            ScopedMembers:[]
          }
        }));

        addUrlSearchParams({ history, location, customViewId: Id, defaultViewId: (lastSelectedViewId ?? Id) + '' });
        setSideViewFilterIndex(Id); //This will be used to set the default selected filter in the side view when we open a ticket from board view with a custom view applied. We are setting it here because we want to apply the custom view filters in the side view as well when we open a ticket from board view.
      }
      else {
        if (location.search) {
          const defaultViewId = getViewIdFromStorage('default') ?? ListInputItemsEnum.All_Active;
          addUrlSearchParams({ history, location, defaultViewId: defaultViewId + '' });
          setState((preState) => ({ ...preState, listFilter: defaultViewId }));
          setCustomViewStates((prev): CustomViewStatesType => ({ ...prev, view: 'none', customViews: [], selectedCustomView: null }));
        }
      }
    } catch (err) {
      console.log('Error while fetching custom views');
    };
  }

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [location.pathname]);


  const fetchTicketData = async () => {
    setState(preState => ({ ...preState, tickets: [], closedTickets: [] }));
    try {
      const results = await Promise.all([
        ((currentstate?.source?.length ?? 0) > 0) ? Promise.resolve(currentstate) : loadFilters(api, graphAPI, currentstate, setState),
        getCachedFeature(LICENSE_FEATURE.TicketTypes),
        getCachedFeature(LICENSE_FEATURE.ServiceLevelAgreements),
        getCachedFeature(LICENSE_FEATURE.MultiDepartment),
        getCachedFeature(PRODUCT_LICENSE.AnalystAccess),
        getCachedFeature(LICENSE_FEATURE.TicketTags),
        getCachedFeature(LICENSE_FEATURE.TicketLifecycle),
        getCachedFeature(LICENSE_FEATURE.Approvals),
        getCachedFeature(LICENSE_FEATURE.ResolutionNote),
        getCachedFeature(LICENSE_FEATURE.GroupChat),
        getCachedFeature(LICENSE_FEATURE.CustomProperties),
        getCachedFeature(LICENSE_FEATURE.TimeTracking)
      ]);

      if (listFeatureStates == undefined){
        let resoConfig = await api.getSystemSetting(ConfigurationName.Resolution).then(response => ({
          ...response.data,
          IsResolutionNoteEnabled: response.data.IsEnabled,
        }));

        setListFeatureStates(prev => ({
           ...prev,
           userResolutionNote: results[8] && resoConfig?.IsEnabled,
           resolutionNoteConfig: resoConfig
         }));
       } 
       
      setIsUserLicensed(results[4]);
      setListFeatureStates(prev => ({
        ...prev,
        useTicketType: results[1],
        useTeam: results[3],
        useTicketLifecycle: results[5],
        useSLA: results[2],
        useGroupChat: results[9],
        useCustomProperties: results[10],
        useTimeTracking: results[11]
      }));

      const [_filters, _defaultFilter] = await prepareFiltersData(results[0],currentstate, t);
       setFilters(_filters);
       setDefaultFilters(_defaultFilter);
      await fetchUserData();

      setIsTicketsFetching(false);
      setIsFiltersFetching(false);
    } catch (err) {
      console.log(`Error occurred when loading filters:`, err);
      setError(err);
    } finally {
      setState(state => ({ ...state, isReload: false }));
    }
  }


  const fetchTicketsHandler = async () => {
    setSelectedTickets([]);
    setIsTicketsFetching(true);
    await loadCustomViews();
    await fetchTicketData();
  };


  const loadCustomViews = async () => {
    await fetchCustomViews();
    const queryValue = await getChildFiltersInputValueFromStorage();
    const applied = await getChildFiltersInputValueFromStorage();
    const count = await getChildFiltersCountFromStorage();
    setState((prevState) => ({ ...prevState,
      filterDropdown: { ...prevState.filterDropdown,
        queryValue: queryValue,
        queryValueApplied: applied,
      },
      filterDropdownCount: count || 0
    }));
  }

  useEffect(() => {
    const checkDeletePermission = () => {
      const roles = JSON.parse(sessionStorage.getItem('my_application_roles') || '[]');
      const hasDeletePermission = Array.isArray(roles) && roles.some(
        (r: any) => r.EndpointPath === '~/api/Ticket' && r.Permissions.includes('DELETE')
      );
      setCanDeleteTickets(hasDeletePermission);
    };

    checkDeletePermission();
    eventBus.on('applicationRolesUpdated', checkDeletePermission);
    return () => eventBus.remove('applicationRolesUpdated', checkDeletePermission);
  }, []);

  useEffect(() => {
    (async() => {
      if (isSignedIn == ConsentState.SignedIn && !isReloadClickedRef.current && !currentstate.isReload) {
        await fetchTicketsHandler();
      }
      if (isSignedIn == ConsentState.SignedIn && currentstate.isReload) {
        isReloadClickedRef.current = true;
        await fetchTicketsHandler();
      }
      if (!currentstate.isReload) {
        isReloadClickedRef.current = false;
      }
    })();

  }, [isSignedIn, currentstate.isReload]);

  // page will change on toggling between "table/board/chart"
  useEffect(() => {
    (async()=>{ 
      const currentPage = page || 'table'; // page 'undefined' means its table page(list view)
      if(currentPage !== toggleButtonMapper[ViewType.chart]) localStorage.setItem('lastTicketPage', currentPage);
      const prevPageRefVariable = prevPageRef.current || 'table';
      if (prevPageRefVariable !== currentPage && currentPage !== toggleButtonMapper[ViewType.chart]) {
        await fetchCustomViews();
      }
      prevPageRef.current = page;
     })();
  }, [page]);

  useEffect(()=>{
    if(customViewStates.selectedCustomView!=null){
      checkBoardScoping();
    }
  },[customViewStates.selectedCustomView]);

  const checkBoardScoping = async () => {
    if (customViewStates.selectedCustomView) {
      const {ScopedMembers,Type} = customViewStates.customViews.find(x=>x.Id==customViewStates.selectedCustomView.Id);
      const enableScoping = ScopedMembers.length>0;
      const isAdminMode = currentstate.me.Claims.includes(`isAdminMode: True`);
      if (Type==CustomViewType.DefaultView || Type==CustomViewType.CompanyView || (enableScoping && (currentstate?.userRoles?.roles?.includes(ADMINISTRATORS) || currentstate?.userRoles?.roles?.includes(ANALYSTS)) && !isAdminMode)
        || (!enableScoping && customViewStates.customViews.some(x=>x.Id==customViewStates.selectedCustomView.Id && x.ModifiedById == currentstate.currentUserId))
        || enableScoping)
      {
        setShowBoard(true);
      }
      else {
        setShowBoard(false);
      }
    }
  }

  const containerClass = checkInTeams() ? `` : `height--50`;
  const scrollContainer = (page === toggleButtonMapper[ViewType.board] || page === toggleButtonMapper[ViewType.chart]) ? 'overflow-scroll' : 'overflow-hidden';

  return (
    <>
      <Helmet>
        <title>{t('ticket.toolbar.tickets')} - Tikit</title>
      </Helmet>
      {hasAccess ? (
        <FluentUIThemeConsumer
          render={(globalTheme) => (
            <ListContextProvider>
              <div className={`h-screen ${containerClass}`}>
                {!isTicketsFetching && (
                  <CustomViewPopup defaultFilters={defaultFilters} globalTheme={globalTheme} isCustomViewEnabled={isCustomViewEnabled} listFeatureStates={listFeatureStates} />
                )}
                <TicketToolbar
                  isCustomViewEnabled={isCustomViewEnabled}
                  isScopingEnabled={isScopingEnabled}
                  isFiltersFetching={isFiltersFetching}
                  isUserLicensed={isUserLicensed}
                  globalTheme={globalTheme}
                  page={page}
                  filters={filters}
                  setFilters={setFilters}
                  defaultFilters={defaultFilters}
                  isEditMode={selectedTickets.length > 0}
                  canDeleteTickets={canDeleteTickets}
                  onEditBulkHandler={() => {
                    setBulkEditDialog(true);
                  }}
                  onDeleteBulkHandler={() => {
                    setBulkDeleteDialog(true);
                  }}
                />
                
                {showBoard && 
                  <TicketToolbarSecondary defaultFilters={defaultFilters} globalTheme={globalTheme} page={page} isCustomViewEnabled={isCustomViewEnabled} listFeatureStates={listFeatureStates}/>
                }

                {!showBoard && <ScopeErrorMessage msgType='NoAccess' />}
                
                
                
                <div className={`${customClassNames.tikitBoardBody} ${scrollContainer} hidden md:block`}>
                  {(windowWidth > 767 && showBoard) && (
                    <TicketBoardView
                      page={page}
                      isTicketsFetching={isTicketsFetching}
                      defaultFilters={defaultFilters}
                      onTicketsSelected={(tickets) => {
                        setSelectedTickets(tickets);
                      }}
                      listFeatureStates={listFeatureStates}
                    />
                  )}
                </div>
                {(windowWidth <= 767 && showBoard) && (
                  <TicketBoardMobileView
                    isTicketsFetching={isTicketsFetching}
                    defaultFilters={defaultFilters}
                  />
                )}
                <BulkEditTicketDialog
                  globalTheme={globalTheme}
                  open={bulkEditDialog}
                  onClose={(value) => {
                    setBulkEditDialog(value);
                  }}
                  tickets={selectedTickets}
                  onSuccessfullyUpdate={(listContext: ListContextType, customViewStates: CustomViewStatesType) => {
                    getpaginatedTicketsOnEdit(
                      listContext.listStates.currentPage,
                      listContext.listStates.countPerPage,
                      listContext,
                      customViewStates
                    );
                  }}
                  listFeatureStates={listFeatureStates}
                />
                <BulkDeleteConfirmDialog
                  globalTheme={globalTheme}
                  open={bulkDeleteDialog}
                  onClose={(value) => {
                    setBulkDeleteDialog(value);
                  }}
                  tickets={selectedTickets}
                  onSuccessfullyDeleted={(listContext: ListContextType, customViewStates: CustomViewStatesType) => {
                    setSelectedTickets([]);
                    getpaginatedTicketsOnEdit(
                      listContext.listStates.currentPage,
                      listContext.listStates.countPerPage,
                      listContext,
                      customViewStates
                    );
                  }}
                />
              </div>
            </ListContextProvider>
          )}
        />
      ) : (
        <AccessDenied />
      )}
    </>
  );
};


function getUniqueUsers(data: PlatformUser[]) {
  function onlyUnique(value: PlatformUser, index: number, self: PlatformUser[]) {
    return (
      self.findIndex((user) => user.UserName === value.UserName) === index
    );
  }
  return data.filter(onlyUnique);
}
